/**
 * Auswahl-Modal fuer Vorlagen mit selection_mode=manual.
 *
 * Wird vom AssistantDrawer geoeffnet wenn die Backend-Antwort
 * `selection_required` enthaelt. Layout: Hybrid — pro source_key ein Block,
 * gruppiert nach group_by (z.B. task_id), Task-Header mit Summe + Checkbox
 * (Alle/Keine), darunter aufklappbare Eintrags-Liste mit Checkboxen pro Zeile.
 *
 * Submit ruft generate_from_template erneut mit `selections` auf.
 */
import { authFetch, useAuth } from '@onvice/portal-shell-api';

import { useState, useMemo } from 'react';
import { X, ChevronDown, ChevronRight, Loader2, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ASSISTANT_BASE =
  (import.meta.env.VITE_ASSISTANT_URL as string | undefined)?.replace(/\/+$/, '') || '/api/modules/assistant';

interface SelectionSource {
  rows: Array<Record<string, unknown>>;
  group_by?: string;
  group_title_field?: string;
  id_field: string;
}

export interface VorlagenSelectionModalProps {
  templateSlug: string;
  dataRefs: Record<string, unknown>;
  sources: Record<string, SelectionSource>;
  onClose: () => void;
  /** Bei Erfolg: optionaler Callback (z.B. Drafts-Refresh). */
  onCreated?: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
  stunden_aufstellung: 'Arbeitszeiten',
  material_aufstellung: 'Material',
};

function mergedRow(
  row: Record<string, unknown>,
  overrides: Record<string, Record<string, Record<string, unknown>>>,
  sourceKey: string,
  idField: string,
): Record<string, unknown> {
  const rid = String(row[idField] ?? '');
  const patch = overrides[sourceKey]?.[rid];
  return patch ? { ...row, ...patch } : row;
}

function sumStunden(
  rows: Array<Record<string, unknown>>,
  overrides: Record<string, Record<string, Record<string, unknown>>>,
  idField: string,
): number {
  return rows.reduce((s, r) => {
    const m = mergedRow(r, overrides, 'stunden_aufstellung', idField);
    return s + (parseFloat(String(m['stunden'] ?? 0)) || 0);
  }, 0);
}

function sumQuantity(
  rows: Array<Record<string, unknown>>,
  overrides: Record<string, Record<string, Record<string, unknown>>>,
  idField: string,
): number {
  return rows.reduce((s, r) => {
    const m = mergedRow(r, overrides, 'material_aufstellung', idField);
    return s + (parseFloat(String(m['quantity'] ?? 0)) || 0);
  }, 0);
}

export default function VorlagenSelectionModal({
  templateSlug,
  dataRefs,
  sources,
  onClose,
  onCreated,
}: VorlagenSelectionModalProps) {
  const { user } = useAuth();
  const effectiveUserId = user?.id ?? '';
  const effectiveEmail = user?.email ?? '';
  const effectiveRole = user?.role ?? 'default';
  const navigate = useNavigate();

  // Selected-IDs pro Source — Default: alles ausgewaehlt
  const [selected, setSelected] = useState<Record<string, Set<string>>>(() => {
    const out: Record<string, Set<string>> = {};
    for (const [key, src] of Object.entries(sources)) {
      out[key] = new Set(src.rows.map((r) => String(r[src.id_field] ?? '')));
    }
    return out;
  });

  // Welche Gruppen sind aufgeklappt? Default: alle zugeklappt.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  // Pro source_key + row-ID die patches die der User im Modal editiert hat.
  const [overrides, setOverrides] = useState<Record<string, Record<string, Record<string, unknown>>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setOverride(sourceKey: string, rowId: string, field: string, value: unknown) {
    setOverrides((prev) => ({
      ...prev,
      [sourceKey]: {
        ...(prev[sourceKey] ?? {}),
        [rowId]: { ...((prev[sourceKey] ?? {})[rowId] ?? {}), [field]: value },
      },
    }));
  }

  // Gruppierung pro Source-Key: { groupKey: { title, rows[] } }
  const grouped = useMemo(() => {
    const out: Record<string, Array<{ groupKey: string; title: string; rows: Array<Record<string, unknown>> }>> = {};
    for (const [key, src] of Object.entries(sources)) {
      const gb = src.group_by;
      const titleField = src.group_title_field;
      const map = new Map<string, { title: string; rows: Array<Record<string, unknown>> }>();
      for (const row of src.rows) {
        const gk = gb ? String(row[gb] ?? '__ungrouped__') : '__all__';
        const title = titleField && row[titleField] != null
          ? String(row[titleField])
          : (gb ? `(${gk.slice(0, 8)}…)` : 'Eintraege');
        if (!map.has(gk)) map.set(gk, { title, rows: [] });
        map.get(gk)!.rows.push(row);
      }
      out[key] = Array.from(map, ([groupKey, v]) => ({ groupKey, title: v.title, rows: v.rows }));
    }
    return out;
  }, [sources]);

  function toggleRow(sourceKey: string, rowId: string) {
    setSelected((prev) => {
      const s = new Set(prev[sourceKey] ?? []);
      if (s.has(rowId)) s.delete(rowId);
      else s.add(rowId);
      return { ...prev, [sourceKey]: s };
    });
  }

  function toggleGroup(sourceKey: string, sourceDef: SelectionSource, groupRows: Array<Record<string, unknown>>) {
    setSelected((prev) => {
      const s = new Set(prev[sourceKey] ?? []);
      const allIn = groupRows.every((r) => s.has(String(r[sourceDef.id_field] ?? '')));
      if (allIn) {
        for (const r of groupRows) s.delete(String(r[sourceDef.id_field] ?? ''));
      } else {
        for (const r of groupRows) s.add(String(r[sourceDef.id_field] ?? ''));
      }
      return { ...prev, [sourceKey]: s };
    });
  }

  function toggleGroupOpen(key: string) {
    setOpenGroups((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return s;
    });
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const selections: Record<string, string[]> = {};
      for (const [key, set] of Object.entries(selected)) {
        selections[key] = Array.from(set);
      }
      const r = await authFetch(`${ASSISTANT_BASE}/assistant/templates/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Portal-User-Id': effectiveUserId,
          'X-Portal-User-Email': effectiveEmail,
          'X-Portal-User-Role': effectiveRole,
        },
        body: JSON.stringify({
          slug: templateSlug,
          data_refs: dataRefs,
          selections,
          overrides,
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status}: ${t.slice(0, 200)}`);
      }
      onCreated?.();
      onClose();
      // Direkt zur Kundenakte navigieren, wenn customer_id bekannt — User soll
      // den Entwurf nicht im Drafts-Tab suchen muessen.
      // KR-5 (Modulanalyse 2026-09-11): /kunden/:id statt /m/stammdaten/kunden —
      // der Stammdaten-Redirect verwirft Query-Parameter, und openCustomer war
      // eine customers-ID im Contact-Raum-Ziel. CustomerDetailPage bridged.
      const cid = dataRefs['customer_id'];
      if (typeof cid === 'string' && cid.length > 0) {
        navigate(`/kunden/${encodeURIComponent(cid)}?tab=akte`);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const totalSelected = Object.values(selected).reduce((sum, s) => sum + s.size, 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card border border-default rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-4 py-3 border-b border-default">
          <h2 className="text-base font-semibold text-foreground">Positionen für Vorlage auswählen</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted" title="Schließen">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-5">
          {Object.entries(sources).map(([sourceKey, sourceDef]) => {
            const groups = grouped[sourceKey] ?? [];
            const selSet = selected[sourceKey] ?? new Set<string>();
            return (
              <section key={sourceKey}>
                <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <span>{SOURCE_LABELS[sourceKey] ?? sourceKey}</span>
                  <span className="text-xs text-muted-foreground">
                    {selSet.size} von {sourceDef.rows.length} gewählt
                  </span>
                </h3>
                {groups.length === 0 && (
                  <div className="text-sm text-muted-foreground italic">Keine Einträge im Zeitraum.</div>
                )}
                <div className="space-y-1.5">
                  {groups.map(({ groupKey, title, rows }) => {
                    const openKey = `${sourceKey}::${groupKey}`;
                    const isOpen = openGroups.has(openKey);
                    const allIn = rows.every((r) => selSet.has(String(r[sourceDef.id_field] ?? '')));
                    const someIn = !allIn && rows.some((r) => selSet.has(String(r[sourceDef.id_field] ?? '')));
                    const summary =
                      sourceKey === 'stunden_aufstellung'
                        ? `${sumStunden(rows, overrides, sourceDef.id_field).toFixed(2)} h, ${rows.length} Einträge`
                        : sourceKey === 'material_aufstellung'
                        ? `${sumQuantity(rows, overrides, sourceDef.id_field).toFixed(2)} Stk, ${rows.length} Einträge`
                        : `${rows.length} Einträge`;
                    return (
                      <div key={openKey} className="border border-default rounded">
                        <div className="flex items-center gap-2 px-2 py-2 hover:bg-muted/40">
                          <input
                            type="checkbox"
                            checked={allIn}
                            ref={(el) => {
                              if (el) el.indeterminate = someIn;
                            }}
                            onChange={() => toggleGroup(sourceKey, sourceDef, rows)}
                            className="size-4"
                          />
                          <button
                            onClick={() => toggleGroupOpen(openKey)}
                            className="flex-1 flex items-center gap-1 text-left text-sm text-foreground"
                          >
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span className="font-medium">{title}</span>
                            <span className="text-xs text-muted-foreground ml-2">— {summary}</span>
                          </button>
                        </div>
                        {isOpen && (
                          <div className="border-t border-default px-3 py-2 space-y-1.5 bg-muted/20">
                            {rows.map((row) => {
                              const id = String(row[sourceDef.id_field] ?? '');
                              const m = mergedRow(row, overrides, sourceKey, sourceDef.id_field);
                              return (
                                <div key={id} className="flex items-center gap-1.5 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={selSet.has(id)}
                                    onChange={() => toggleRow(sourceKey, id)}
                                    className="size-3.5 shrink-0"
                                  />
                                  {sourceKey === 'stunden_aufstellung' && (
                                    <>
                                      <span className="text-muted-foreground w-20 shrink-0">{String(m['datum'] ?? '').slice(0, 10)}</span>
                                      <input
                                        type="text"
                                        value={String(m['beschreibung'] ?? '')}
                                        onChange={(e) => setOverride(sourceKey, id, 'beschreibung', e.target.value)}
                                        className="flex-1 bg-card border border-default rounded px-1.5 py-0.5 text-xs"
                                        placeholder="Beschreibung"
                                      />
                                      <input
                                        type="number"
                                        step="0.25"
                                        value={String(m['stunden'] ?? 0)}
                                        onChange={(e) => setOverride(sourceKey, id, 'stunden', parseFloat(e.target.value) || 0)}
                                        className="w-16 bg-card border border-default rounded px-1.5 py-0.5 text-xs text-right"
                                      />
                                      <span className="text-muted-foreground w-3">h</span>
                                      <input
                                        type="number"
                                        step="0.5"
                                        value={String(m['ep'] ?? 0)}
                                        onChange={(e) => setOverride(sourceKey, id, 'ep', parseFloat(e.target.value) || 0)}
                                        className="w-16 bg-card border border-default rounded px-1.5 py-0.5 text-xs text-right"
                                      />
                                      <span className="text-muted-foreground w-4">€/h</span>
                                    </>
                                  )}
                                  {sourceKey === 'material_aufstellung' && (
                                    <>
                                      <input
                                        type="text"
                                        value={String(m['label'] ?? '')}
                                        onChange={(e) => setOverride(sourceKey, id, 'label', e.target.value)}
                                        className="flex-1 bg-card border border-default rounded px-1.5 py-0.5 text-xs"
                                        placeholder="Bezeichnung"
                                      />
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={String(m['quantity'] ?? 0)}
                                        onChange={(e) => setOverride(sourceKey, id, 'quantity', parseFloat(e.target.value) || 0)}
                                        className="w-16 bg-card border border-default rounded px-1.5 py-0.5 text-xs text-right"
                                      />
                                      <input
                                        type="text"
                                        value={String(m['unit'] ?? '')}
                                        onChange={(e) => setOverride(sourceKey, id, 'unit', e.target.value)}
                                        className="w-12 bg-card border border-default rounded px-1.5 py-0.5 text-xs"
                                      />
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={String(m['unit_price'] ?? 0)}
                                        onChange={(e) => setOverride(sourceKey, id, 'unit_price', parseFloat(e.target.value) || 0)}
                                        className="w-16 bg-card border border-default rounded px-1.5 py-0.5 text-xs text-right"
                                      />
                                      <span className="text-muted-foreground w-3">€</span>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {error && (
          <div className="px-4 py-2 border-t border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300 text-xs">
            {error}
          </div>
        )}

        <footer className="flex items-center justify-between px-4 py-3 border-t border-default">
          <span className="text-xs text-muted-foreground">{totalSelected} Position(en) ausgewählt</span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="text-sm px-3 py-1.5 rounded border border-default hover:bg-muted disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={() => void submit()}
              disabled={submitting || totalSelected === 0}
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Entwurf erstellen
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
